import { __unsafe_useEmotionCache, CacheProvider } from '@emotion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { PortalDocumentContext } from '#components/datasheet-popover/portal-document-context.js';

import type { EmotionCache } from '@emotion/react';
import type { ReactNode } from 'react';

const MIRRORED_STYLE_ATTRIBUTE = 'data-datasheet-popover-mirrored-style';
const POPUP_LAYER_ORDER_STYLE_ID = 'datasheet-popover-layer-order';
const STYLE_NODE_SELECTOR = 'link[rel~="stylesheet"], style';
const POPUP_CLOSE_POLL_INTERVAL_MS = 500;
const POPUP_EMOTION_CACHE_KEY = 'datasheet-popover';
const APP_CSS_LAYER_ORDER = '@layer base, mui, theme, components, utilities;';

/**
 * Props for {@link ExternalWindowPortal}.
 */
export type ExternalWindowPortalProps = {
	externalWindow: Window;
	title: string;
	children: ReactNode;
	onClose: () => void;
};

/**
 * Renders React children into a same-origin popup window while preserving the opener's React tree.
 *
 * The children mount via a portal into a root element inside the popup document, so they keep the
 * opener's React context, state, and reconciliation. The opener's stylesheets and color scheme are
 * mirrored into the popup, and Emotion styles are redirected to the popup's `<head>` so MUI renders
 * correctly there. `onClose` fires once when the popup closes; on unmount the popup is also closed.
 *
 * @param props.externalWindow - The already-opened, same-origin popup to render into.
 * @param props.title - The popup document title; kept in sync as it changes.
 * @param props.children - The React subtree to render inside the popup.
 * @param props.onClose - Invoked exactly once when the popup is closed by the user or torn down.
 * @returns A portal into the popup once its root element is ready, otherwise `null`.
 */
export function ExternalWindowPortal({ externalWindow, title, children, onClose }: ExternalWindowPortalProps) {
	const baseEmotionCache = __unsafe_useEmotionCache();
	const onCloseRef = useRef(onClose);
	const initialTitleRef = useRef(title);
	const pendingCloseTimerRef = useRef<number | null>(null);
	const didCloseExternalWindowRef = useRef(false);
	const [container, setContainer] = useState<HTMLElement | null>(null);

	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (pendingCloseTimerRef.current !== null) {
			window.clearTimeout(pendingCloseTimerRef.current);
			pendingCloseTimerRef.current = null;
		}

		if (externalWindow.closed) {
			onCloseRef.current();
			return undefined;
		}

		const popupDocument = externalWindow.document;
		const rootElement = preparePopupDocument(popupDocument, initialTitleRef.current);
		const cleanupMirroredStyles = mirrorDocumentStyles(document, popupDocument);
		const cleanupColorScheme = syncColorScheme(document, popupDocument);

		let isDisposed = false;
		let didNotifyClose = false;
		const notifyClosed = () => {
			if (didNotifyClose) {
				return;
			}
			didNotifyClose = true;
			onCloseRef.current();
		};

		const handleBeforeUnload = () => {
			if (!isDisposed) {
				notifyClosed();
			}
		};

		externalWindow.addEventListener('beforeunload', handleBeforeUnload);
		const closePollId = window.setInterval(() => {
			if (externalWindow.closed) {
				notifyClosed();
			}
		}, POPUP_CLOSE_POLL_INTERVAL_MS);

		setContainer(rootElement);

		return () => {
			isDisposed = true;
			window.clearInterval(closePollId);
			externalWindow.removeEventListener('beforeunload', handleBeforeUnload);
			rootElement.remove();
			cleanupMirroredStyles();
			cleanupColorScheme();

			if (didCloseExternalWindowRef.current) {
				return;
			}

			if (pendingCloseTimerRef.current !== null) {
				window.clearTimeout(pendingCloseTimerRef.current);
			}

			pendingCloseTimerRef.current = window.setTimeout(() => {
				pendingCloseTimerRef.current = null;

				if (didCloseExternalWindowRef.current) {
					return;
				}

				didCloseExternalWindowRef.current = true;

				if (!externalWindow.closed) {
					externalWindow.close();
				}
			}, 0);
		};
	}, [externalWindow]);

	useEffect(() => {
		if (!externalWindow.closed) {
			setPopupDocumentTitle(externalWindow, title);
		}
	}, [externalWindow, title]);

	const popupEmotionCache = useMemo(() => {
		if (baseEmotionCache === null || container === null) {
			return null;
		}
		return createPopupEmotionCache(baseEmotionCache, container.ownerDocument);
	}, [baseEmotionCache, container]);

	if (container === null) {
		return null;
	}

	const contents = (
		<PortalDocumentContext value={container.ownerDocument}>
			{popupEmotionCache === null ? children : <CacheProvider value={popupEmotionCache}>{children}</CacheProvider>}
		</PortalDocumentContext>
	);

	return createPortal(contents, container);
}

/**
 * Initializes a freshly-opened popup document and creates the portal mount point.
 *
 * Sets the title, applies full-height/no-scroll layout to the document and body, installs the CSS
 * layer-order declaration, and appends a flex-column root element for the portal to render into.
 *
 * @param popupDocument - The popup window's document to prepare.
 * @param title - The initial document title.
 * @returns The root element the portal should mount into.
 */
function preparePopupDocument(popupDocument: Document, title: string): HTMLElement {
	popupDocument.title = title;
	popupDocument.documentElement.style.height = '100%';
	popupDocument.documentElement.style.overflow = 'hidden';
	ensurePopupLayerOrderStyle(popupDocument);

	Object.assign(popupDocument.body.style, {
		height: '100%',
		margin: '0',
		minHeight: '0',
		overflow: 'hidden',
	});

	const rootElement = popupDocument.createElement('div');
	rootElement.id = 'datasheet-popover-external-root';
	Object.assign(rootElement.style, {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: '0',
		width: '100%',
	});
	popupDocument.body.appendChild(rootElement);
	return rootElement;
}

/**
 * Ensures the popup's CSS cascade-layer ordering is declared before any mirrored styles.
 *
 * The layer-order declaration must be the first node in `<head>` so layered rules from the opener
 * resolve in the intended precedence. Idempotent: an existing declaration is moved back to the front
 * rather than duplicated.
 *
 * @param popupDocument - The popup window's document to install the declaration into.
 */
function ensurePopupLayerOrderStyle(popupDocument: Document) {
	const existingLayerOrderStyle = popupDocument.getElementById(POPUP_LAYER_ORDER_STYLE_ID);
	if (existingLayerOrderStyle !== null) {
		popupDocument.head.insertBefore(existingLayerOrderStyle, popupDocument.head.firstChild);
		return;
	}

	const layerOrderStyle = popupDocument.createElement('style');
	layerOrderStyle.id = POPUP_LAYER_ORDER_STYLE_ID;
	layerOrderStyle.textContent = APP_CSS_LAYER_ORDER;
	popupDocument.head.insertBefore(layerOrderStyle, popupDocument.head.firstChild);
}

/**
 * Mirrors the source document's `<head>` stylesheets into the target document and keeps them in sync.
 *
 * Clones every `<link rel="stylesheet">` and `<style>` from the source `<head>` into the target, then
 * watches the source `<head>` and re-mirrors on any change so the popup tracks live style updates
 * (e.g. Vite HMR, dynamically inserted rules).
 *
 * @param sourceDocument - The opener document whose styles are mirrored.
 * @param targetDocument - The popup document to mirror styles into.
 * @returns A cleanup function that stops watching and removes all mirrored nodes.
 */
function mirrorDocumentStyles(sourceDocument: Document, targetDocument: Document): () => void {
	const mirroredNodes: ChildNode[] = [];

	const removeMirroredNodes = () => {
		while (mirroredNodes.length > 0) {
			mirroredNodes.pop()?.remove();
		}
	};

	const syncStyles = () => {
		removeMirroredNodes();

		for (const sourceNode of sourceDocument.head.querySelectorAll<HTMLLinkElement | HTMLStyleElement>(
			STYLE_NODE_SELECTOR,
		)) {
			const mirroredNode = cloneStyleNode(sourceNode, targetDocument);
			if (mirroredNode === null) {
				continue;
			}

			mirroredNode.setAttribute(MIRRORED_STYLE_ATTRIBUTE, '');
			targetDocument.head.appendChild(mirroredNode);
			mirroredNodes.push(mirroredNode);
		}
	};

	syncStyles();

	const MutationObserverConstructor = sourceDocument.defaultView?.MutationObserver ?? MutationObserver;
	const observer = new MutationObserverConstructor(syncStyles);
	observer.observe(sourceDocument.head, {
		attributes: true,
		childList: true,
		characterData: true,
		subtree: true,
	});

	return () => {
		observer.disconnect();
		removeMirroredNodes();
	};
}

/**
 * Clones a single stylesheet node into another document, preserving its attributes and content.
 *
 * `<link>` nodes are cloned with their resolved `href`; `<style>` nodes are cloned with their
 * serialized rule text so the popup gets the live, post-cascade CSS rather than only static markup.
 *
 * @param sourceNode - The `<link>` or `<style>` element to clone.
 * @param targetDocument - The document that owns the cloned node.
 * @returns The cloned node, or `null` if it could not be cloned.
 */
function cloneStyleNode(
	sourceNode: HTMLLinkElement | HTMLStyleElement,
	targetDocument: Document,
): HTMLLinkElement | HTMLStyleElement | null {
	if (sourceNode instanceof HTMLLinkElement) {
		const link = targetDocument.createElement('link');
		copyAttributes(sourceNode, link);
		link.href = sourceNode.href;
		return link;
	}

	const style = targetDocument.createElement('style');
	copyAttributes(sourceNode, style);
	style.textContent = getStyleSheetText(sourceNode);
	return style;
}

/**
 * Copies every attribute from one element onto another.
 *
 * @param sourceElement - The element to read attributes from.
 * @param targetElement - The element to write attributes onto.
 */
function copyAttributes(sourceElement: Element, targetElement: Element) {
	for (const attribute of sourceElement.attributes) {
		targetElement.setAttribute(attribute.name, attribute.value);
	}
}

/**
 * Serializes a `<style>` element's effective CSS to text.
 *
 * Prefers the parsed `cssRules` so runtime-inserted rules (e.g. Emotion) are captured, and falls back
 * to raw `textContent` when the sheet's rules are inaccessible.
 *
 * @param styleElement - The `<style>` element to serialize.
 * @returns The element's CSS text, or an empty string if none is available.
 */
function getStyleSheetText(styleElement: HTMLStyleElement): string {
	try {
		const cssRules = styleElement.sheet?.cssRules;
		if (cssRules !== undefined) {
			return Array.from(cssRules, (rule) => rule.cssText).join('\n');
		}
	} catch {
		// Cross-origin stylesheets can throw on cssRules access; textContent is the best fallback.
	}

	return styleElement.textContent ?? '';
}

/**
 * Mirrors the source document's `data-color-scheme` attribute onto the target and keeps it in sync.
 *
 * Copies the attribute immediately, then watches the source `<html>` element so the popup follows
 * subsequent light/dark theme switches in the opener.
 *
 * @param sourceDocument - The opener document whose color scheme is mirrored.
 * @param targetDocument - The popup document to apply the color scheme to.
 * @returns A cleanup function that stops watching the source.
 */
function syncColorScheme(sourceDocument: Document, targetDocument: Document): () => void {
	const sync = () => {
		const colorScheme = sourceDocument.documentElement.getAttribute('data-color-scheme');
		if (colorScheme === null) {
			targetDocument.documentElement.removeAttribute('data-color-scheme');
		} else {
			targetDocument.documentElement.setAttribute('data-color-scheme', colorScheme);
		}
	};

	sync();

	const MutationObserverConstructor = sourceDocument.defaultView?.MutationObserver ?? MutationObserver;
	const observer = new MutationObserverConstructor(sync);
	observer.observe(sourceDocument.documentElement, {
		attributeFilter: ['data-color-scheme'],
		attributes: true,
	});

	return () => observer.disconnect();
}

/**
 * Derives an Emotion cache that inserts styles into the popup document instead of the opener.
 *
 * The returned cache reuses the base cache's configuration and registered styles but routes new style
 * tags through a {@link PopupEmotionStyleSheet} targeting the popup's `<head>`, so MUI/Emotion
 * components rendered in the popup are styled there while still being serialized by the base cache.
 *
 * @param baseCache - The opener's Emotion cache to derive from.
 * @param targetDocument - The popup document whose `<head>` should receive the styles.
 * @returns An Emotion cache scoped to the popup document.
 */
function createPopupEmotionCache(baseCache: EmotionCache, targetDocument: Document): EmotionCache {
	const sheet = new PopupEmotionStyleSheet({
		key: POPUP_EMOTION_CACHE_KEY,
		container: targetDocument.head,
		nonce: baseCache.sheet.nonce,
		speedy: baseCache.sheet.isSpeedy,
	}) as unknown as EmotionCache['sheet'];
	const popupCache: EmotionCache = {
		...baseCache,
		key: POPUP_EMOTION_CACHE_KEY,
		inserted: {},
		registered: { ...baseCache.registered },
		sheet,
		insert: (selector, serialized, targetSheet, shouldCache) => {
			const insertableSerialized = { ...serialized };
			const result = baseCache.insert(selector, insertableSerialized, targetSheet, false);
			if (shouldCache) {
				popupCache.inserted[serialized.name] = true;
			}
			return result;
		},
	};

	return popupCache;
}

/**
 * A minimal Emotion `StyleSheet` replacement that inserts `<style>` tags into a popup document.
 *
 * Emotion's built-in sheet is bound to the opener document; this stand-in mirrors its public shape
 * (`insert`/`flush`/`hydrate` and the fields Emotion reads) while creating and appending tags in the
 * container's owning document, so styles land in the popup's `<head>`.
 */
class PopupEmotionStyleSheet {
	before: Element | null = null;
	container: Node;
	ctr = 0;
	insertionPoint?: HTMLElement;
	isSpeedy: boolean;
	key: string;
	nonce?: string;
	prepend?: boolean;
	tags: HTMLStyleElement[] = [];
	private _alreadyInsertedOrderInsensitiveRule = false;

	/**
	 * @param options.key - The Emotion cache key, written as the tags' `data-emotion` attribute.
	 * @param options.container - The node (typically the popup `<head>`) that style tags are inserted into.
	 * @param options.nonce - Optional CSP nonce applied to created style tags.
	 * @param options.speedy - Whether Emotion's speedy (CSSOM) insertion mode is enabled.
	 */
	constructor({
		key,
		container,
		nonce,
		speedy = false,
	}: {
		key: string;
		container: Node;
		nonce?: string;
		speedy?: boolean;
	}) {
		this.key = key;
		this.container = container;
		this.nonce = nonce;
		this.isSpeedy = speedy;
	}

	/**
	 * Adopts pre-existing style tags, keeping only those belonging to the popup document.
	 *
	 * @param nodes - Candidate style tags to adopt.
	 */
	hydrate(nodes: HTMLStyleElement[]) {
		const targetDocument = getNodeDocument(this.container);
		this.tags.push(...nodes.filter((node) => node.ownerDocument === targetDocument));
	}

	/**
	 * Inserts a single CSS rule, appending it to the current style tag (creating one if needed).
	 *
	 * @param rule - The CSS rule text to insert.
	 */
	insert(rule: string) {
		const tag = this.tags.at(-1) ?? this.createStyleTag();
		tag.appendChild(tag.ownerDocument.createTextNode(rule));
		this.ctr++;
	}

	/**
	 * Removes all inserted style tags and resets the sheet to its empty state.
	 */
	flush() {
		for (const tag of this.tags) {
			tag.remove();
		}
		this.tags = [];
		this.ctr = 0;
		this._alreadyInsertedOrderInsensitiveRule = false;
	}

	/**
	 * Creates a new `<style>` tag in the container's document and appends it before the insertion point.
	 *
	 * @returns The newly created style tag.
	 */
	private createStyleTag(): HTMLStyleElement {
		const targetDocument = getNodeDocument(this.container);
		const tag = targetDocument.createElement('style');
		tag.setAttribute('data-emotion', this.key);
		if (this.nonce !== undefined) {
			tag.setAttribute('nonce', this.nonce);
		}

		this.container.insertBefore(tag, this.before);
		this.tags.push(tag);
		return tag;
	}
}

/**
 * Resolves the document a node belongs to, treating a document node as its own owner.
 *
 * @param node - The node to resolve the owning document for.
 * @returns The node's document.
 * @throws If the node is not a document and has no owner document.
 */
function getNodeDocument(node: Node): Document {
	if (node.nodeType === 9) {
		return node as Document;
	}

	const ownerDocument = node.ownerDocument;
	if (ownerDocument === null) {
		throw new Error('Cannot create popup Emotion styles without an owner document.');
	}
	return ownerDocument;
}

/**
 * Sets the popup window's document title.
 *
 * @param externalWindow - The popup window to update.
 * @param title - The title to apply.
 */
function setPopupDocumentTitle(externalWindow: Window, title: string) {
	externalWindow.document.title = title;
}
